import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell } from "lucide-react";
import { api } from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export const timeAgo = (iso) => {
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))} min`;
  if (s < 86400) return `${Math.floor(s / 3600)} h`;
  return new Date(iso).toLocaleDateString("pt-BR");
};

export const NotificationBell = () => {
  const [data, setData] = useState({ items: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get("/notifications").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);
  const go = async (n) => {
    if (!n.read) await api.post(`/notifications/${n.id}/read`);
    setOpen(false);
    load();
    if (n.link) navigate(n.link);
  };
  const readAll = async () => { await api.post("/notifications/read-all"); load(); };
  const has = data.unread > 0;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" aria-label="Notificações" className={`relative flex h-10 w-10 items-center justify-center rounded-full border ${has ? "border-[#E4F222] bg-[#E4F222]/10" : "border-[#232B3E]"}`}>
          <Bell data-testid={has ? "bell-unread" : "bell-read"} className={`h-5 w-5 ${has ? "fill-[#E4F222] text-[#E4F222]" : "text-[#94A3B8]"}`} />
          {has && <span data-testid="notification-count" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-[#E4F222] px-1 text-[10px] font-black text-black">{data.unread > 9 ? "9+" : data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] max-w-[92vw] border-[#232B3E] bg-[#121722] p-0">
        <div className="flex items-center justify-between border-b border-[#232B3E] px-4 py-3">
          <span className="h-display text-xl">Notificações</span>
          {has && <button data-testid="notifications-read-all" onClick={readAll} className="text-xs font-semibold text-[#E4F222]">Marcar todas como lidas</button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {data.items.length === 0 ? <p className="p-4 text-sm text-[#64748B]">Nenhuma notificação.</p> : data.items.slice(0, 20).map((n) => (
            <button key={n.id} data-testid={`notification-${n.id}`} onClick={() => go(n)} className={`flex w-full gap-3 border-b border-[#232B3E] px-4 py-3 text-left hover:bg-[#1A202E] ${n.read ? "" : "bg-[#E4F222]/5"}`}>
              <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.read ? "bg-transparent" : "bg-[#E4F222]"}`} />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold">{n.title}</span>
                {n.body && <span className="block text-xs text-[#94A3B8]">{n.body}</span>}
                <span className="block text-[10px] text-[#64748B]">{timeAgo(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
        <button data-testid="notifications-settings-link" onClick={() => { setOpen(false); navigate("/app/notificacoes"); }} className="w-full px-4 py-3 text-center text-xs font-semibold text-[#94A3B8] hover:text-white">Ver todas e preferências</button>
      </PopoverContent>
    </Popover>
  );
};
